import { Instant, Temporal } from "@/Temporal"
import { AbstractApiSchema, mapApiType, type ApiSchema, type ApiTypeOf } from "vue-utils"
import { ObjectPathSchema, getNestedSchema, type ObjectPath } from "./path"

export interface BaseAction {
	type: string
	guid: string
	created: Instant
}

export interface AddItemAction<T = unknown> extends BaseAction {
	type: "Add"
	path: ObjectPath
	value: T
}

export interface SetFieldValueAction<T = unknown> extends BaseAction {
	type: "Set"
	path: ObjectPath
	newValue: T
}

export interface RemoveFieldAction extends BaseAction {
	type: "Remove"
	path: ObjectPath
}

export type ModificationAction = AddItemAction | SetFieldValueAction | RemoveFieldAction

export function isModificationAction(action: BaseAction): action is ModificationAction {
	return action.type === "Add" || action.type === "Set" || action.type === "Remove"
}

export class ModificationActionSchema<T extends ModificationAction> extends AbstractApiSchema<T> {
	constructor(readonly rootValueSchema: ApiSchema<unknown>) {
		super()
	}

	override convert(action: ApiTypeOf<T>): T {
		const path = mapApiType(ObjectPathSchema, action.path)
		const commonData = {
			created: action.created ? Instant.from(action.created) : Temporal.Now.instant(),
			guid: action.guid ?? crypto.randomUUID(),
			path,
		}

		const nestedSchema = getNestedSchema(this.rootValueSchema, path)

		switch (action.type) {
			case "Add":
				return {
					...commonData,
					type: "Add",
					value: mapApiType(nestedSchema, action.value),
				} satisfies AddItemAction as T
			case "Set":
				return {
					...commonData,
					type: "Set",
					newValue: mapApiType(nestedSchema, action.newValue),
				} satisfies SetFieldValueAction as T
			case "Remove":
				return {
					...commonData,
					type: "Remove",
				} satisfies RemoveFieldAction as T
		}
		throw new Error("Unknown action type")
	}
}
